import { createApi } from '@reduxjs/toolkit/query/react'
import {
  CreateTripPayload,
  CreateTripResponse,
  EditTripPayload,
  ListTripsPayload,
  ListTripsResponse,
  TripPayload,
} from './trips.types'
import { transformListTrips } from './trips.utils'
import { createBaseQueryWithReauth } from 'store/auth/base-query-with-reauth'

const baseUrl = `${process.env.REACT_APP_ORDER_SERVICE_API}`

/** Backend expects numeric order PK as orderId (not userOrderId). */
const toTripTaskRequestBody = (task: TripPayload) => ({
  type: task.type,
  orderEventId: task.orderEventId,
  orderId: task.sourceOrderPkId,
  tractorId: task.tractorId,
  trailerId: task.trailerId,
  name: task.name,
  address: task.address,
  startTime: task.startTime,
  estimatedTime: task.estimatedTime,
  sequenceId: task.sequenceId,
  tripTaskId: task.tripTaskId,
  tripId: task.tripId,
})

const toCreateTripRequestBody = (payload: CreateTripPayload) => ({
  ...payload,
  tripTasks: payload.tripTasks.map(toTripTaskRequestBody),
})

const toEditTripRequestBody = (payload: EditTripPayload) => ({
  ...payload,
  tripTasks: payload.tripTasks.map(toTripTaskRequestBody),
})

export const tripsApi = createApi({
  reducerPath: 'tripsApi',
  baseQuery: createBaseQueryWithReauth(baseUrl!),
  endpoints: (builder) => ({
    createTrip: builder.mutation<CreateTripResponse, CreateTripPayload>({
      query: (body) => ({
        url: 'osapi/v1/trips',
        method: 'POST',
        headers: {
          userId: window.sessionStorage.getItem('userId') ?? '',
        },
        body: toCreateTripRequestBody(body),
      }),
    }),

    listTrips: builder.query<ListTripsResponse, ListTripsPayload>({
      query: ({ size, page, sortBy, status, tripVariant }) => {
        const variantParam = tripVariant ? `&tripVariant=${tripVariant}` : ''
        return {
          url: `osapi/v1/trips?size=${size}&page=${page}&sortBy=${sortBy}&status=${status}${variantParam}`,
          method: 'GET',
          headers: {
            userId: window.sessionStorage.getItem('userId') ?? '',
          },
        }
      },
      transformResponse: (response) =>
        transformListTrips(response as ListTripsResponse),
    }),

    editTrip: builder.mutation<CreateTripResponse, EditTripPayload>({
      query: (body) => ({
        url: `osapi/v1/trips/${body.tripId}`,
        method: 'PUT',
        body: toEditTripRequestBody(body),
      }),
    }),

    endTrip: builder.mutation<EditTripPayload, { tripId: string }>({
      query: ({ tripId }) => ({
        url: `osapi/v1/trips/${tripId}/end`,
        method: 'POST',
      }),
    }),

    deleteTrip: builder.mutation<void, { tripId: string }>({
      query: ({ tripId }) => ({
        url: `osapi/v1/trips/${tripId}`,
        method: 'DELETE',
        headers: {
          userId: window.sessionStorage.getItem('userId') ?? '',
        },
      }),
    }),

    updateTripTaskStatus: builder.mutation<
      any,
      { tripId: number; taskId: number; status: string }
    >({
      query: ({ tripId, taskId, status }) => ({
        url: `osapi/v1/trips/${tripId}/tasks/${taskId}/status`,
        method: 'PATCH',
        body: { status },
      }),
    }),
  }),
})

export const {
  useCreateTripMutation,
  useListTripsQuery,
  useLazyListTripsQuery,
  useEditTripMutation,
  useEndTripMutation,
  useDeleteTripMutation,
  useUpdateTripTaskStatusMutation,
} = tripsApi
